import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Sun, Moon, ArrowLeft, Users, Target, Trophy, ClipboardList,
  TrendingUp, Shield, ShieldCheck, Calendar, Activity, Trash2, AlertTriangle
} from 'lucide-react';

// Colors for charts
const COLORS = {
  q1: '#10b981', // emerald
  q2: '#f59e0b', // amber
  q3: '#f59e0b', // amber
  q4: '#f43f5e', // rose
  primary: '#6366f1',
  secondary: '#8b5cf6',
  accent: '#ec4899'
};

const BENEFICIARY_COLORS = {
  me: '#10b981',
  work: '#3b82f6',
  others: '#8b5cf6',
  no_one: '#6b7280'
};

export const AdminDashboardPage = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [trends, setTrends] = useState([]);
  const [wellbeing, setWellbeing] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAllData is stable, runs when user/navigate change
  }, [user, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, usersRes, trendsRes, wellbeingRes] = await Promise.all([
        api.get('/admin/analytics/overview'),
        api.get('/admin/analytics/users'),
        api.get('/admin/analytics/trends'),
        api.get('/admin/analytics/wellbeing')
      ]);
      
      setOverview(overviewRes.data);
      setUsers(usersRes.data);
      setTrends(trendsRes.data);
      setWellbeing(wellbeingRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}/toggle-admin`, {});
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, is_admin: !currentStatus } : u
      ));
      toast.success(`Admin status ${!currentStatus ? 'granted' : 'revoked'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteDialog.user.user_id}`);
      setUsers(prev => prev.filter(u => u.user_id !== deleteDialog.user.user_id));
      // Update overview count
      if (overview) {
        setOverview(prev => ({ ...prev, total_users: prev.total_users - 1 }));
      }
      toast.success(`User "${deleteDialog.user.name || deleteDialog.user.email}" has been deleted`);
      setDeleteDialog({ open: false, user: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  // Prepare chart data
  const quadrantData = overview ? [
    { name: 'High Att / High Imp', value: overview.quadrant_distribution.q1, color: COLORS.q1 },
    { name: 'High Att / Low Imp', value: overview.quadrant_distribution.q2, color: COLORS.q2 },
    { name: 'Low Att / High Imp', value: overview.quadrant_distribution.q3, color: COLORS.q3 },
    { name: 'Low Att / Low Imp', value: overview.quadrant_distribution.q4, color: COLORS.q4 }
  ] : [];

  const beneficiaryData = overview ? [
    { name: 'Me', value: overview.beneficiary_breakdown.me, color: BENEFICIARY_COLORS.me },
    { name: 'Work', value: overview.beneficiary_breakdown.work, color: BENEFICIARY_COLORS.work },
    { name: 'Others', value: overview.beneficiary_breakdown.others, color: BENEFICIARY_COLORS.others },
    { name: 'No One', value: overview.beneficiary_breakdown.no_one, color: BENEFICIARY_COLORS.no_one }
  ] : [];

  const wellbeingChartData = wellbeing?.averages ? [
    { metric: 'Control', value: wellbeing.averages.control },
    { metric: 'Productivity', value: wellbeing.averages.productivity },
    { metric: 'Efficiency', value: wellbeing.averages.efficiency },
    { metric: 'Contentment', value: wellbeing.averages.contentment },
    { metric: 'Self Esteem', value: wellbeing.averages.self_esteem },
    { metric: 'Concentration', value: wellbeing.averages.concentration },
    { metric: 'Work-Life', value: wellbeing.averages.work_life_balance }
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-rose-500 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold font-['Manrope']">Admin Dashboard</h1>
              <p className="text-xs text-muted-foreground">Population Analytics</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Badge variant="outline" className="gap-1">
            <Shield className="w-3 h-3" />
            Admin
          </Badge>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 md:p-6 max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-lg">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends">Trends</TabsTrigger>
            <TabsTrigger value="wellbeing" data-testid="tab-wellbeing">Wellbeing</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Total Users
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.total_users || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    +{overview?.users_this_week || 0} this week
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <Target className="w-4 h-4" />
                    Total Interests
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.total_interests || 0}</div>
                  <p className="text-xs text-muted-foreground">Active interests</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <Trophy className="w-4 h-4" />
                    Achievements
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.total_achievements || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    +{overview?.achievements_this_week || 0} this week
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    <ClipboardList className="w-4 h-4" />
                    Surveys
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overview?.total_surveys || 0}</div>
                  <p className="text-xs text-muted-foreground">Completed surveys</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Quadrant Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Interest Distribution by Quadrant</CardTitle>
                  <CardDescription>Where users place their interests</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={quadrantData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {quadrantData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Beneficiary Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Achievement Beneficiaries</CardTitle>
                  <CardDescription>Who benefits from completed interests</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={beneficiaryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {beneficiaryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  User Management
                </CardTitle>
                <CardDescription>
                  {users.length} registered users
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="text-center">Interests</TableHead>
                        <TableHead className="text-center">Achievements</TableHead>
                        <TableHead className="text-center">Joined</TableHead>
                        <TableHead className="text-center">Admin</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(u => (
                        <TableRow key={u.user_id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-xs font-medium">{u.name?.charAt(0) || '?'}</span>
                                </div>
                              )}
                              <span className="font-medium">{u.name || 'Unknown'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                          <TableCell className="text-center">{u.interest_count}</TableCell>
                          <TableCell className="text-center">{u.achievement_count}</TableCell>
                          <TableCell className="text-center text-sm text-muted-foreground">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={u.is_admin}
                              onCheckedChange={() => handleToggleAdmin(u.user_id, u.is_admin)}
                              disabled={u.email === 'brian@pwkpr.com'}
                              data-testid={`admin-toggle-${u.user_id}`}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteDialog({ open: true, user: u })}
                              disabled={u.email === 'brian@pwkpr.com' || u.user_id === user?.user_id}
                              title={u.email === 'brian@pwkpr.com' ? 'Cannot delete initial admin' : u.user_id === user?.user_id ? 'Cannot delete yourself' : 'Delete user'}
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Activity Trends (Last 30 Days)
                </CardTitle>
                <CardDescription>Daily user registrations, interests, and achievements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 10 }}
                        tickFormatter={(val) => val.slice(5)} // Show MM-DD
                      />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="users" 
                        name="New Users"
                        stroke={COLORS.primary} 
                        fill={COLORS.primary} 
                        fillOpacity={0.3}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="interests" 
                        name="New Interests"
                        stroke={COLORS.secondary} 
                        fill={COLORS.secondary} 
                        fillOpacity={0.3}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="achievements" 
                        name="Achievements"
                        stroke={COLORS.q1} 
                        fill={COLORS.q1} 
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>This Week</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Users</span>
                      <span className="font-medium">+{overview?.users_this_week || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Achievements</span>
                      <span className="font-medium">+{overview?.achievements_this_week || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>This Month</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Users</span>
                      <span className="font-medium">+{overview?.users_this_month || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Achievements</span>
                      <span className="font-medium">+{overview?.achievements_this_month || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>All Time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Users</span>
                      <span className="font-medium">{overview?.total_users || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Achievements</span>
                      <span className="font-medium">{overview?.total_achievements || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Wellbeing Tab */}
          <TabsContent value="wellbeing" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Average Scores */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Average Wellbeing Scores
                  </CardTitle>
                  <CardDescription>
                    Based on {wellbeing?.total_surveys || 0} submitted surveys
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={wellbeingChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="metric" tick={{ fontSize: 10 }} width={80} />
                        <Tooltip />
                        <Bar dataKey="value" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Score Cards */}
              <Card>
                <CardHeader>
                  <CardTitle>Detailed Scores</CardTitle>
                  <CardDescription>Population average (0-10 scale)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {wellbeingChartData.map(item => (
                      <div key={item.metric} className="p-3 rounded-lg bg-muted/50">
                        <div className="text-xs text-muted-foreground">{item.metric}</div>
                        <div className="text-xl font-bold">{item.value.toFixed(1)}</div>
                        <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${item.value * 10}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Trend */}
            {wellbeing?.trend && wellbeing.trend.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Monthly Wellbeing Trends
                  </CardTitle>
                  <CardDescription>How wellbeing metrics change over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={wellbeing.trend}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="control" name="Control" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />
                        <Area type="monotone" dataKey="productivity" name="Productivity" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                        <Area type="monotone" dataKey="contentment" name="Contentment" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ open, user: open ? deleteDialog.user : null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete User Account
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {deleteDialog.user && (
            <div className="py-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                {deleteDialog.user.picture ? (
                  <img src={deleteDialog.user.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium">{deleteDialog.user.name?.charAt(0) || '?'}</span>
                  </div>
                )}
                <div>
                  <p className="font-medium">{deleteDialog.user.name || 'Unknown'}</p>
                  <p className="text-sm text-muted-foreground">{deleteDialog.user.email}</p>
                </div>
              </div>
              <div className="mt-3 text-sm text-muted-foreground">
                <p>This will permanently delete:</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li>{deleteDialog.user.interest_count} interest(s)</li>
                  <li>{deleteDialog.user.achievement_count} achievement(s)</li>
                  <li>All surveys and activity logs</li>
                </ul>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, user: null })}
              disabled={deleting}
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
